import type { ActivityDefinition, Extension, ObservationDefinition } from "@medplum/fhirtypes";
import {
  resolveParameters,
  STANDARD_OBSERVATIONS,
  type TreatmentComponentValue,
  type TreatmentObservationDeclaration,
} from "@repo/domain";
import { CATALOG_VALUE_URL, decodeCatalogValue } from "../fhir/catalog-value";
import { isStandardParameterExtension, readStandardParameterReference } from "../fhir/standard-parameter-reference";
import {
  childText,
  extension,
  extensions,
  TREATMENT_COMPONENT_URL,
  TREATMENT_DEVICE_REQUIREMENT_URL,
  TREATMENT_OBSERVATION_NOTE_LABEL_URL,
  TREATMENT_OBSERVATION_REPEATABLE_URL,
  TREATMENT_PARAMETER_URL,
  treatmentDefinitionAvailability,
  treatmentDefinitionDescription,
  treatmentDefinitionLocationRequirement,
  treatmentDefinitionMintedAt,
  treatmentDefinitionName,
  treatmentDefinitionObservations,
  treatmentDefinitionProcedureNoteLabel,
  treatmentDefinitionStandardObservations,
  treatmentDefinitionUrl,
  treatmentDefinitionVersion,
} from "../fhir/treatment-definition-storage";
import type { TreatmentDefinitionDto, TreatmentValueDefinitionDto } from "./treatment-definition";

/**
 * A member the stored row cannot express is DROPPED, not thrown on. This runs
 * inside a live query, where a throw blanks the whole page; a row written wrong
 * is a server defect and the server is where it fails loudly.
 */
type Observation = TreatmentDefinitionDto["observations"][number];
type Component = Observation["components"][number];

function projectComponent(candidate: Extension): Component | undefined {
  const id = childText(candidate, "id");
  const label = childText(candidate, "label");
  const value = decodeCatalogValue(extension(candidate.extension, CATALOG_VALUE_URL));
  if (!id || label === undefined || !value) return undefined;
  return { id, label, value };
}

function projectObservation(definition: ObservationDefinition): Observation | undefined {
  const id = definition.id;
  const panel = definition.code.coding?.find((coding) => coding.display !== undefined);
  const label = definition.preferredReportName ?? panel?.display;
  const repeatable = extension(definition.extension, TREATMENT_OBSERVATION_REPEATABLE_URL)?.valueBoolean;
  const noteLabel = extension(definition.extension, TREATMENT_OBSERVATION_NOTE_LABEL_URL)?.valueString;
  if (!id || label === undefined || repeatable === undefined) return undefined;
  return {
    id,
    label,
    repeatable,
    ...(noteLabel === undefined ? {} : { noteLabel }),
    components: extensions(definition.extension, TREATMENT_COMPONENT_URL).flatMap((candidate) => {
      const component = projectComponent(candidate);
      return component ? [component] : [];
    }),
  };
}

function projectParameter(candidate: Extension): TreatmentDefinitionDto["parameters"][number] | undefined {
  if (isStandardParameterExtension(candidate)) return readStandardParameterReference(candidate);
  const id = childText(candidate, "id");
  const label = childText(candidate, "label");
  const value = decodeCatalogValue(extension(candidate.extension, CATALOG_VALUE_URL));
  if (!id || label === undefined || !value) return undefined;
  return { id, label, value };
}

function projectDeviceRequirement(
  candidate: Extension,
): TreatmentDefinitionDto["deviceRequirements"][number] | undefined {
  const id = childText(candidate, "id");
  // Unpinned on purpose: the requirement names a DeviceDefinition LINEAGE.
  const definition = extension(candidate.extension, "definition")?.valueCanonical;
  if (!id || !definition) return undefined;
  return { id, definition };
}

export function projectTreatmentDefinitionRow(resource: ActivityDefinition): TreatmentDefinitionDto {
  const description = treatmentDefinitionDescription(resource);
  const procedureNoteLabel = treatmentDefinitionProcedureNoteLabel(resource);
  const locationRequirement = treatmentDefinitionLocationRequirement(resource);
  const standardObservations = treatmentDefinitionStandardObservations(resource);
  const observations = treatmentDefinitionObservations(resource).flatMap((definition) => {
    const observation = projectObservation(definition);
    return observation ? [observation] : [];
  });
  const parameters = extensions(resource.extension, TREATMENT_PARAMETER_URL).flatMap((candidate) => {
    const parameter = projectParameter(candidate);
    return parameter ? [parameter] : [];
  });
  return {
    url: treatmentDefinitionUrl(resource),
    ...(resource.id ? { resourceId: resource.id } : {}),
    version: treatmentDefinitionVersion(resource),
    mintedAt: treatmentDefinitionMintedAt(resource),
    name: treatmentDefinitionName(resource),
    ...(description === undefined ? {} : { description }),
    availability: treatmentDefinitionAvailability(resource),
    ...(procedureNoteLabel === undefined ? {} : { procedureNoteLabel }),
    standardObservations,
    observations,
    // What this treatment charts: platform standards first, then authored.
    chartedObservations: [
      ...standardObservations.map((key) => projectTreatmentObservation(STANDARD_OBSERVATIONS[key])),
      ...observations,
    ],
    effectiveParameters: resolveParameters(parameters),
    parameters,
    deviceRequirements: extensions(resource.extension, TREATMENT_DEVICE_REQUIREMENT_URL).flatMap((candidate) => {
      const requirement = projectDeviceRequirement(candidate);
      return requirement ? [requirement] : [];
    }),
    ...(locationRequirement === undefined ? {} : { locationRequirement }),
  };
}

function projectValue(value: TreatmentComponentValue): TreatmentValueDefinitionDto {
  if (value.kind !== "Choice") return { ...value };
  return {
    ...value,
    options: value.options.map((option) => ({ id: String(option.id), label: option.label })),
  };
}

/**
 * A platform-owned declaration, which arrives as a domain object rather than a
 * stored row: standards are never persisted as authored members.
 */
export function projectTreatmentObservation(observation: TreatmentObservationDeclaration): Observation {
  return {
    ...observation,
    components: observation.components.map((component) => ({ ...component, value: projectValue(component.value) })),
  };
}
